import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import CardWithTitle from 'components/CardWithTitle';
import ButtonV2 from 'components/common/ButtonV2';
import { setOrderDetails } from 'actions/orderDetailsAction';
import { authContext } from 'contexts/AuthContext';
import { axiosUpdate } from 'services';

const ACCEPT_OPERATION = 1;
const REJECT_OPERATION = 0;
const DISMISSED_ORDER_REQUEST_IDS_KEY = 'orders_dismissed_request_ids';

const readDismissedOrderRequestIds = () => {
  try {
    const rawValue = window.localStorage.getItem(
      DISMISSED_ORDER_REQUEST_IDS_KEY,
    );
    if (!rawValue) return [];

    const parsedValue = JSON.parse(rawValue);
    if (!Array.isArray(parsedValue)) return [];

    return parsedValue.map(String);
  } catch (error) {
    return [];
  }
};

const writeDismissedOrderRequestIds = (ids) => {
  try {
    const uniqueIds = Array.from(new Set(ids.map(String)));
    window.localStorage.setItem(
      DISMISSED_ORDER_REQUEST_IDS_KEY,
      JSON.stringify(uniqueIds),
    );
  } catch (error) {
    // Ignore localStorage failures.
  }
};

const formatAddress = (address) => {
  if (!address || typeof address === 'string') {
    return typeof address === 'string' ? address.trim() : '';
  }

  const parts = [
    address?.houseNumber,
    address?.street,
    address?.area,
    address?.city?.cityName || address?.city?.name || address?.city,
    address?.state?.stateName || address?.state?.name || address?.state,
    address?.country?.country || address?.country?.name || address?.country,
    address?.pinCode,
  ].filter((part) => part && String(part).trim() !== '');

  return parts.join(', ');
};

const resolveOrderAddress = (order) => {
  const deliveryAddressText = formatAddress(order?.deliveryAddress);
  if (deliveryAddressText) {
    return {
      addressText: deliveryAddressText,
      source: 'Delivery address',
    };
  }

  const billingAddressText = formatAddress(order?.billingAddress);
  if (billingAddressText) {
    return {
      addressText: billingAddressText,
      source: 'Billing address',
    };
  }

  if (order?.buyerGstBillingAddress) {
    const buyerBillingText = String(order?.buyerGstBillingAddress).trim();
    if (buyerBillingText) {
      return {
        addressText: buyerBillingText,
        source: 'Buyer billing address',
      };
    }
  }

  if (order?.pickupAddressText) {
    const pickupText = String(order?.pickupAddressText).trim();
    if (pickupText) {
      return {
        addressText: pickupText,
        source: 'Pickup address',
      };
    }
  }

  return {
    addressText: '',
    source: 'Address not available',
  };
};

const OrderRequest = ({ order }) => {
  const dispatch = useDispatch();
  const { auth } = useContext(authContext);
  const [loadingOperation, setLoadingOperation] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isRequestHidden, setIsRequestHidden] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setIsRequestHidden(false);
    setIsDismissed(
      order?.id
        ? readDismissedOrderRequestIds().includes(String(order.id))
        : false,
    );
  }, [order?.id]);

  const isPendingRequest =
    order?.status === 'PENDING' &&
    !['ORDER_APPROVED', 'ORDER_REJECTED', 'ORDER_APPROVAL_EXPIRED'].includes(
      order?.approvalStatus,
    ) &&
    order?.sellerActionTaken !== true &&
    !isDismissed;

  if (!isPendingRequest || isRequestHidden) {
    return null;
  }

  const { addressText } = resolveOrderAddress(order);

  const closeOrderDrawer = () => {
    dispatch(setOrderDetails({ status: false, selectedOrder: null }));
  };

  const refreshOrders = () => {
    window.dispatchEvent(new Event('orderCreated'));
  };

  const handleOrderRequest = async (operation) => {
    if (!order?.id) {
      toast.error('Order not available');
      return;
    }

    if (!auth?.data?.token) {
      toast.error('Authorization token not available');
      return;
    }

    try {
      setLoadingOperation(operation);

      const response = await axiosUpdate(
        `${process.env.REACT_APP_REST_API_ENDPOINT}order/accept-or-reject-order`,
        {
          orderId: order?.id,
          operation,
          pickupAddressId: null,
          pickupAddressText: addressText || null,
        },
        auth?.data?.token,
      );

      if (response?.isSuccessful) {
        const currentOrderId = String(order?.id);
        const existingIds = readDismissedOrderRequestIds();
        writeDismissedOrderRequestIds([...existingIds, currentOrderId]);

        setIsRequestHidden(true);
        setIsDismissed(true);
        toast.success(
          operation === ACCEPT_OPERATION
            ? 'Order accepted successfully'
            : 'Order rejected successfully',
        );
        refreshOrders();
        closeOrderDrawer();
        return;
      }

      const errorMessage =
        response?.data?.message ||
        response?.data?.error ||
        response?.message ||
        'Something went wrong.';
      toast.error(errorMessage);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Something went wrong.',
      );
    } finally {
      if (isMountedRef.current) {
        setLoadingOperation(null);
      }
    }
  };

  return (
    <CardWithTitle title="Order Request(Seller Approval)" className="mt-3">
      <div className="border rounded-8 p-3">
        <div className="d-flex align-items-start gap-2">
          <div
            className="d-flex align-items-start gap-2 flex-grow-1"
            style={{ minWidth: 0 }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                marginTop: 6,
                backgroundColor: '#c0392b',
                flexShrink: 0,
              }}
            />
            <div className="d-flex flex-column gap-1">
              <div className="fw-semibold">
                A buyer requested to place order
              </div>
              <div className="f-12 text-gray mt-1">
                <span className="fw-medium text-dark">Address:</span>{' '}
                {addressText || 'Not available'}
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap mt-3 ms-4">
          <ButtonV2
            type="button"
            size="sm"
            color="danger"
            className="border-radius-4 px-3"
            disabled={loadingOperation !== null}
            onClick={() => handleOrderRequest(REJECT_OPERATION)}
          >
            {loadingOperation === REJECT_OPERATION ? 'Rejecting...' : 'Reject'}
          </ButtonV2>
          <ButtonV2
            type="button"
            size="sm"
            color="success"
            className="border-radius-4 px-3"
            disabled={loadingOperation !== null}
            onClick={() => handleOrderRequest(ACCEPT_OPERATION)}
          >
            {loadingOperation === ACCEPT_OPERATION ? 'Accepting...' : 'Accept'}
          </ButtonV2>
        </div>
      </div>
    </CardWithTitle>
  );
};

export default OrderRequest;
